import React, { useState, useEffect } from 'react';
import { Key, Moon, Sun, Monitor, Eye, EyeOff, GraduationCap, Plus, Trash2, Volume2 } from 'lucide-react';
import { useTheme } from '@/components/providers/ThemeProvider';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/store/authStore';
import { useVoiceoverStore } from '@/store/voiceoverStore';
import { VOICEOVER_AVATARS } from '@/config/voiceoverAvatars';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import PageHeader from '@/components/PageHeader';
import ConfirmDialog from '@/components/common/ConfirmDialog';

interface Setting {
  id: number;
  key: string;
  value: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export const SettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const { theme, setTheme } = useTheme();
  const { isCurator, user } = useAuthStore();
  const { selectedAvatarId, selectedVoiceURI, voiceoverVolume, setSelectedAvatar, setSelectedVoice, setVoiceoverVolume } = useVoiceoverStore();
  const [mbti, setMbti] = useState<{ d1: 'I' | 'E'; d2: 'N' | 'S'; d3: 'T' | 'F'; d4: 'J' | 'P' } | null>(null);
  const [universities, setUniversities] = useState<Array<{ id: number; name: string }>>([]);
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedUniversity, setSelectedUniversity] = useState<string>('');
  const [newUniversityName, setNewUniversityName] = useState<string>('');
  const [deletingUniversityId, setDeletingUniversityId] = useState<number | null>(null);
  const [universityToDelete, setUniversityToDelete] = useState<{ id: number; name: string } | null>(null);

  useEffect(() => {
    // Only fetch settings if user is a curator
    if (isCurator()) {
      fetchSettings();
    } else {
      setLoading(false);
    }
    // Fetch universities for both curators and students
    fetchUniversities();
    // Fetch user profile to get current university
    fetchUserProfile();
  }, [isCurator]);

  useEffect(() => {
    // Initialize MBTI from localStorage for students
    try {
      const stored = localStorage.getItem('student_mbti');
      if (stored) {
        const val = JSON.parse(stored);
        if (val && val.d1 && val.d2 && val.d3 && val.d4) {
          setMbti(val);
        }
      } else {
        // Default unselected state selects first of each pair for preview, but not saved until user acts
        setMbti({ d1: 'I', d2: 'N', d3: 'T', d4: 'J' });
      }
    } catch {
      setMbti({ d1: 'I', d2: 'N', d3: 'T', d4: 'J' });
    }
  }, []);

  useEffect(() => {
    // Fetch current profile and hydrate MBTI if saved in backend
    const hydrateFromProfile = async () => {
      try {
        if (isCurator()) return; // curators don't have MBTI in settings
        const profile = await authService.getProfile();
        const user = (profile as any)?.user || profile; // tolerate either { user } or user directly
        const value: string | undefined = user?.mbti || user?.user?.mbti;
        if (value && /^[IE][NS][TF][JP]$/.test(value)) {
          setMbti({ d1: value[0] as 'I' | 'E', d2: value[1] as 'N' | 'S', d3: value[2] as 'T' | 'F', d4: value[3] as 'J' | 'P' });
        }
      } catch (e) {
        // ignore profile fetch errors; page still usable
      }
    };
    hydrateFromProfile();
  }, [isCurator]);

  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      return;
    }

    const loadVoices = () => {
      setAvailableVoices(window.speechSynthesis.getVoices());
    };

    loadVoices();
    window.speechSynthesis.addEventListener?.('voiceschanged', loadVoices);
    window.speechSynthesis.onvoiceschanged = loadVoices;

    return () => {
      window.speechSynthesis.removeEventListener?.('voiceschanged', loadVoices);
      if (window.speechSynthesis.onvoiceschanged === loadVoices) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const token = authService.getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await fetch('/api/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Find and set the API key
        if (data.settings && Array.isArray(data.settings)) {
          const apiKeySetting = data.settings.find((s: Setting) => s.key === 'deepseek_api_key');
          if (apiKeySetting) {
            setApiKey(apiKeySetting.value);
          }
        } else {
          setMessage({ type: 'error', text: 'Invalid settings data format' });
        }
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error fetching settings' });
    } finally {
      setLoading(false);
    }
  };

  const saveApiKey = async () => {
    try {
      setSaving(true);
      const token = authService.getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await fetch('/api/settings/deepseek_api_key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          value: apiKey,
          description: 'DeepSeek API key for LLM evaluation features'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setMessage({ type: 'success', text: data.message });
        // Refresh settings to get current values
        await fetchSettings();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to save API key' });
      }
    } catch (error) {
      console.error('Error saving API key:', error);
      setMessage({ type: 'error', text: 'Error saving API key' });
    } finally {
      setSaving(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      const token = authService.getToken();
      const response = await fetch('/api/universities', {
        headers: token ? {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        } : {}
      });

      if (response.ok) {
        const data = await response.json();
        setUniversities(data.universities || []);
      }
    } catch (error) {
      console.error('Failed to fetch universities:', error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await authService.getProfile();
      const userData = (profile as any)?.user || profile;
      if (userData?.university) {
        setSelectedUniversity(userData.university);
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const saveUniversity = async () => {
    try {
      setSaving(true);
      const token = authService.getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const profile = await authService.getProfile();
      const userData = (profile as any)?.user || profile;

      const response = await fetch('/api/auth/profile', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: userData?.name || user?.name,
          email: userData?.email || userData?.username || user?.username,
          mbti: mbti ? `${mbti.d1}${mbti.d2}${mbti.d3}${mbti.d4}` : userData?.mbti,
          university: selectedUniversity
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'University updated successfully' });
        await fetchUserProfile();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to save university' });
      }
    } catch (error) {
      console.error('Error saving university:', error);
      setMessage({ type: 'error', text: 'Error saving university' });
    } finally {
      setSaving(false);
    }
  };

  const addUniversity = async () => {
    if (!newUniversityName.trim()) {
      setMessage({ type: 'error', text: 'University name is required' });
      return;
    }

    try {
      setSaving(true);
      const token = authService.getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await fetch('/api/universities', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newUniversityName.trim()
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'University added successfully' });
        setNewUniversityName('');
        await fetchUniversities();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to add university' });
      }
    } catch (error) {
      console.error('Error adding university:', error);
      setMessage({ type: 'error', text: 'Error adding university' });
    } finally {
      setSaving(false);
    }
  };

  const deleteUniversity = async (id: number) => {
    try {
      setDeletingUniversityId(id);
      const token = authService.getToken();
      if (!token) {
        setMessage({ type: 'error', text: 'Authentication required' });
        return;
      }

      const response = await fetch(`/api/universities/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'University deleted successfully' });
        setUniversityToDelete(null);
        await fetchUniversities();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.error || 'Failed to delete university' });
      }
    } catch (error) {
      console.error('Error deleting university:', error);
      setMessage({ type: 'error', text: 'Error deleting university' });
    } finally {
      setDeletingUniversityId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[100svh] bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <LoadingSpinner size="xl" />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Loading Settings
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Please wait while we load your settings...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] w-full bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-100/30 dark:from-gray-900 dark:via-slate-800 dark:to-slate-900">
      <PageHeader
        subtitle={isCurator() 
          ? 'Manage your application configuration and preferences'
          : 'Customize your application appearance and preferences'
        }
        icon={Key}
        iconColor="text-blue-600 dark:text-blue-400"
        iconBgColor="bg-blue-100 dark:bg-blue-900/20"
        showBackButton={false}
        variant="settings"
      />
      <div 
        className="w-full py-2 sm:py-3 sm:pb-4 overflow-y-auto h-[100svh]"
        style={{
          paddingTop: 'var(--header-height, 80px)'
        }}
      >
        {/* Settings Content */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
          <div className="mb-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isCurator() 
                ? 'Manage your application configuration and preferences'
                : 'Customize your application appearance and preferences'
              }
            </p>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`mb-4 p-3 ${
              message.type === 'success' 
                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                : message.type === 'error'
                ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
                : 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* University Management Section - Only visible to curators */}
          {isCurator() && (
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <GraduationCap className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  University Management
                </h3>
              </div>

              <div className="p-4 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <label htmlFor="newUniversity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Add New University
                  </label>
                  <div className="flex space-x-2">
                    <input
                      id="newUniversity"
                      type="text"
                      value={newUniversityName}
                      onChange={(e) => setNewUniversityName(e.target.value)}
                      placeholder="Enter university name"
                      className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-200"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addUniversity();
                        }
                      }}
                    />
                    <button
                      onClick={addUniversity}
                      disabled={saving || !newUniversityName.trim()}
                      className="inline-flex items-center px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium transition-colors duration-200 border border-transparent rounded-lg"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Existing Universities
                  </label>
                  {universities.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No universities added yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {universities.map((uni) => (
                        <div key={uni.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <span className="text-sm text-gray-900 dark:text-white">{uni.name}</span>
                          <button
                            onClick={() => setUniversityToDelete(uni)}
                            disabled={deletingUniversityId === uni.id}
                            className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 transition-colors"
                          >
                            {deletingUniversityId === uni.id ? (
                              <LoadingSpinner size="sm" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* API Key Section - Only visible to curators */}
          {isCurator() && (
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <Key className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  API Key Management
                </h3>
              </div>

              <div className="p-4 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    DeepSeek API Key
                  </label>
                  <div className="relative">
                    <input
                      id="apiKey"
                      type={showApiKey ? 'text' : 'password'}
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your DeepSeek API key"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {showApiKey ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={saveApiKey}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm font-medium transition-colors duration-200 border border-transparent"
                  >
                    {saving ? 'Saving...' : 'Save API Key'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Dark Mode Section - Visible to all users */}
          <div className="mb-2">
            <div className="flex items-center mb-3">
              <Moon className="h-4 w-4 text-purple-600 dark:text-purple-400 mr-2" />
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Appearance Settings
              </h3>
            </div>
            
            <div className="p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
              </p>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Theme
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex flex-col items-center p-2 border transition-all duration-200 ${
                        theme === 'light'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <Sun className={`h-5 w-5 mb-1 ${
                        theme === 'light' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                      }`} />
                      <span className={`text-xs font-medium ${
                        theme === 'light' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        Light
                      </span>
                    </button>
                    
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex flex-col items-center p-2 border transition-all duration-200 ${
                        theme === 'dark'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <Moon className={`h-5 w-5 mb-1 ${
                        theme === 'dark' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                      }`} />
                      <span className={`text-xs font-medium ${
                        theme === 'dark' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        Dark
                      </span>
                    </button>
                    
                    <button
                      onClick={() => setTheme('system')}
                      className={`flex flex-col items-center p-2 border transition-all duration-200 ${
                        theme === 'system'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      <Monitor className={`h-5 w-5 mb-1 ${
                        theme === 'system' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
                      }`} />
                      <span className={`text-xs font-medium ${
                        theme === 'system' ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        System
                      </span>
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    System theme automatically follows your operating system's appearance setting.
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                  <div className="flex items-center mb-3">
                    <Volume2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                    <label htmlFor="voiceover-voice" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      AI voice
                    </label>
                  </div>

                  <select
                    id="voiceover-voice"
                    value={selectedVoiceURI}
                    onChange={(event) => {
                      const voice = availableVoices.find(item => item.voiceURI === event.target.value);
                      setSelectedVoice(voice ? {
                        voiceURI: voice.voiceURI,
                        name: voice.name,
                        lang: voice.lang,
                      } : null);
                    }}
                    disabled={availableVoices.length === 0}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-60"
                  >
                    <option value="">
                      {availableVoices.length === 0 ? 'No browser voices available' : 'System default'}
                    </option>
                    {availableVoices.map((voice) => (
                      <option key={voice.voiceURI} value={voice.voiceURI}>
                        {voice.name} ({voice.lang}){voice.default ? ' - default' : ''}
                      </option>
                    ))}
                  </select>

                  <div className="mt-4">
                    <p className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Voice avatar
                    </p>
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {VOICEOVER_AVATARS.map((avatar) => {
                        const isSelected = selectedAvatarId === avatar.id;

                        return (
                          <button
                            key={avatar.id}
                            type="button"
                            onClick={() => setSelectedAvatar(avatar.id)}
                            className={`flex items-center gap-3 border px-3 py-2 text-left transition-colors ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                                : 'border-gray-200 text-gray-700 hover:border-gray-300 dark:border-gray-600 dark:text-gray-300 dark:hover:border-gray-500'
                            }`}
                            aria-pressed={isSelected}
                          >
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800">
                              <img src={avatar.imageSrc} alt="" className="h-full w-full object-contain" />
                            </span>
                            <span className="text-sm font-medium">{avatar.name}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="mb-2 flex items-center justify-between">
                      <label htmlFor="voiceover-volume" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Voice volume
                      </label>
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {Math.round(voiceoverVolume * 100)}%
                      </span>
                    </div>
                    <input
                      id="voiceover-volume"
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={voiceoverVolume}
                      onChange={(event) => setVoiceoverVolume(Number(event.target.value))}
                      className="h-1.5 w-full cursor-pointer accent-blue-600"
                      aria-label="AI voiceover volume"
                    />
                  </div>
                </div>
              </div>
            </div>

          {/* MBTI Preference - Students only */}
          {!isCurator() && (
            <div className="mt-6">
              <div className="flex items-center mb-3">
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Personality Preference (MBTI)
                </h3>
              </div>
              <div className="p-4 border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  Select one letter from each column.
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {/* Column 1: I / E */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d1: 'I' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d1 === 'I' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      I
                    </button>
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'E', d2: 'N', d3: 'T', d4: 'J' }), d1: 'E' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d1 === 'E' ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      E
                    </button>
                  </div>
                  {/* Column 2: N / S */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d2: 'N' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d2 === 'N' ? 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      N
                    </button>
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'S', d3: 'T', d4: 'J' }), d2: 'S' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d2 === 'S' ? 'bg-green-50 dark:bg-green-900/20 border-green-500 text-green-700 dark:text-green-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      S
                    </button>
                  </div>
                  {/* Column 3: T / F */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d3: 'T' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d3 === 'T' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-700 dark:text-purple-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      T
                    </button>
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'F', d4: 'J' }), d3: 'F' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d3 === 'F' ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-500 text-purple-700 dark:text-purple-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      F
                    </button>
                  </div>
                  {/* Column 4: J / P */}
                  <div className="flex flex-col space-y-2">
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'J' }), d4: 'J' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d4 === 'J' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      J
                    </button>
                    <button
                      onClick={() => setMbti(prev => ({ ...(prev || { d1: 'I', d2: 'N', d3: 'T', d4: 'P' }), d4: 'P' }))}
                      className={`py-3 text-sm font-semibold border transition-colors ${mbti?.d4 === 'P' ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500 text-amber-700 dark:text-amber-300' : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'}`}
                    >
                      P
                    </button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Selected: <span className="font-semibold">{`${mbti?.d1 || ''}${mbti?.d2 || ''}${mbti?.d3 || ''}${mbti?.d4 || ''}`}</span>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        if (!mbti?.d1 || !mbti?.d2 || !mbti?.d3 || !mbti?.d4) {
                          setMessage({ type: 'error', text: 'Please select one letter from each column.' });
                          return;
                        }
                        const value = `${mbti.d1}${mbti.d2}${mbti.d3}${mbti.d4}`;
                        localStorage.setItem('student_mbti', JSON.stringify(mbti));
                        await authService.updateProfile({ mbti: value });
                        setMessage({ type: 'success', text: 'Personality preference saved.' });
                      } catch (e) {
                        setMessage({ type: 'error', text: 'Failed to save preference.' });
                      }
                    }}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-colors duration-200"
                  >
                    Save Preference
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* University Selection - Students only */}
          {!isCurator() && (
            <div className="mt-6">
              <div className="flex items-center mb-3">
                <GraduationCap className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  University
                </h3>
              </div>
              <div className="p-4 border border-gray-200 dark:border-gray-700">
                <div className="mb-4">
                  <label htmlFor="university" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Your University
                  </label>
                  <select
                    id="university"
                    value={selectedUniversity}
                    onChange={(e) => setSelectedUniversity(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors duration-200"
                  >
                    <option value="">Select your university</option>
                    {universities.map((uni) => (
                      <option key={uni.id} value={uni.name}>
                        {uni.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={saveUniversity}
                  disabled={saving || !selectedUniversity}
                  className="inline-flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white text-sm font-medium transition-colors duration-200 border border-transparent"
                >
                  {saving ? 'Saving...' : 'Save University'}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        isOpen={Boolean(universityToDelete)}
        title="Delete University"
        message={
          <>
            Delete <span className="font-medium">{universityToDelete?.name}</span>? Students assigned to it will need to select a new university.
          </>
        }
        confirmLabel="Delete"
        loading={deletingUniversityId === universityToDelete?.id}
        variant="danger"
        onCancel={() => setUniversityToDelete(null)}
        onConfirm={() => {
          if (universityToDelete) {
            deleteUniversity(universityToDelete.id);
          }
        }}
      />
    </div>
  );
};

